import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { publicApi, wsUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open || !user || conv) return;
    (async () => {
      const { data } = await publicApi.get("/chat/my-conversation");
      setConv(data);
      const { data: msgs } = await publicApi.get(`/chat/conversations/${data.id}/messages`);
      setMessages(msgs);
    })();
  }, [open, user, conv]);

  useEffect(() => {
    if (!conv) return;
    const ws = new WebSocket(wsUrl(`/ws/chat/${conv.id}`));
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    };
    wsRef.current = ws;
    return () => ws.close();
  }, [conv]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = () => {
    const t = text.trim();
    if (!t || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ sender: "customer", sender_name: user?.name || "Customer", text: t }));
    setText("");
  };

  if (!user) return null;

  return (
    <>
      <button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full btn-gold !p-0 shadow-xl"
        aria-label="Chat with us"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div data-testid="chat-widget-panel" className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[28rem] bg-ink-800 border border-gold/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fade-up">
          <div className="bg-gold-deep px-4 py-3 border-b border-gold/40">
            <p className="font-serif text-cream text-lg">Concierge Chat</p>
            <p className="text-xs text-sterling">We usually reply within minutes</p>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <p className="text-muted2 text-sm text-center mt-8">Send us a message — our team is here to help plan your event.</p>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${m.sender === "customer" ? "bg-gold text-ink" : "bg-ink-700 text-cream border border-gold/20"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="p-3 border-t border-gold/20 flex gap-2">
            <input
              data-testid="chat-widget-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type a message…"
              className="flex-1 bg-ink border border-gold/25 rounded-full px-4 py-2 text-sm text-cream focus:outline-none focus:border-gold"
            />
            <button data-testid="chat-widget-send" onClick={send} className="btn-gold !p-0 w-10 h-10 rounded-full">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
